import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import Section from './components/Section'
import StatTilesCard, { StatTileItem } from './components/StatTilesCard'
import { AXIS_TICK, CHART_COLORS, TOOLTIP_STYLE } from './constants'
import { OPERATION_PRODUCT_ANALYTICS_PRIVATE_WIFI } from './operations-gql/queries'
import {
  PrivateWifiActions,
  PrivateWifiQuery,
  PrivateWifiRooms,
  ProductAnalyticsPeriod,
  ProductAnalyticsVariables,
} from './types'

type PrivateWifiSectionProps = {
  period: ProductAnalyticsPeriod
}

type RoomAction = {
  labelKey: string
  click: keyof PrivateWifiActions
  completed: keyof PrivateWifiActions
}

const TRANSLATION_PREFIX = 'stats.product_analytics.private_wifi'

const ROOM_ACTIONS: RoomAction[] = [
  { labelKey: 'creations', click: 'creations', completed: 'created' },
  { labelKey: 'updates', click: 'updates', completed: 'updated' },
  { labelKey: 'deletions', click: 'deletions', completed: 'deleted' },
  { labelKey: 'resets', click: 'resets', completed: 'passphraseReset' },
]

const ROOM_TILES: (keyof PrivateWifiRooms)[] = ['created', 'imported', 'deleted', 'reset']

const PrivateWifiSection: React.FC<PrivateWifiSectionProps> = ({ period }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<PrivateWifiQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_PRIVATE_WIFI,
    { variables: { period }, errorPolicy: 'all' }
  )
  const actions = data?.productAnalytics?.privateWifiActions
  const rooms = data?.productAnalytics?.privateWifiRooms
  const imports = data?.productAnalytics?.privateWifiImports

  const roomActions = useMemo(() => (
    ROOM_ACTIONS.map(({ labelKey, click, completed }) => ({
      label: t(`${TRANSLATION_PREFIX}.room_actions.${labelKey}`),
      clicks: actions?.[click] ?? 0,
      completed: actions?.[completed] ?? 0,
    }))
  ), [actions, t])

  const hasFieldError = (field: unknown) => Boolean(error) && !field

  const roomTiles = ROOM_TILES.map(key => ({
    key,
    label: t(`${TRANSLATION_PREFIX}.rooms.${key}`),
    value: rooms?.[key],
  }))

  const importTiles: StatTileItem[] = [
    { key: 'imports', label: t(`${TRANSLATION_PREFIX}.imports.imports`), value: imports?.imports },
    { key: 'errorRows', label: t(`${TRANSLATION_PREFIX}.imports.error_rows`), value: imports?.errorRows },
    {
      key: 'importsWithErrorsRate',
      label: t(`${TRANSLATION_PREFIX}.imports.imports_with_errors_rate`),
      value: imports?.importsWithErrorsRate,
      suffix: ' %',
    },
  ]

  return (
    <Section id="product-analytics-private-wifi" title={t(`${TRANSLATION_PREFIX}.title`)} columns={{ xs: 1, md: 3 }}>
      <ChartCard
        title={t(`${TRANSLATION_PREFIX}.room_actions_title`)}
        description={t(`${TRANSLATION_PREFIX}.room_actions_description`)}
        loading={loading}
        hasError={hasFieldError(actions)}
        isEmpty={!roomActions.some(({ clicks, completed }) => clicks > 0 || completed > 0)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={roomActions}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} interval={0} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="clicks"
              name={t(`${TRANSLATION_PREFIX}.clicks`)}
              fill={CHART_COLORS.secondary}
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="completed"
              name={t(`${TRANSLATION_PREFIX}.completed`)}
              fill={CHART_COLORS.tertiary}
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <StatTilesCard
        title={t(`${TRANSLATION_PREFIX}.rooms_title`)}
        description={t(`${TRANSLATION_PREFIX}.rooms_description`)}
        tiles={roomTiles}
        loading={loading}
        hasError={hasFieldError(rooms)}
      />

      <StatTilesCard
        title={t(`${TRANSLATION_PREFIX}.imports_title`)}
        description={t(`${TRANSLATION_PREFIX}.imports_description`)}
        tiles={importTiles}
        loading={loading}
        hasError={hasFieldError(imports)}
      />
    </Section>
  )
}

export default PrivateWifiSection
